import { useEffect, useMemo, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { useTranslation } from "@/i18n"
import { useRelayInfo, useRelayReviews } from "@/hooks/useRelayData"
import { useTimeline } from "@/hooks/useTimeline"
import { usePublish } from "@/hooks/usePublish"
import { useProfiles } from "@/hooks/useProfiles"
import { useRelays } from "@/state/RelayContext"
import { useNostr } from "@/state/NostrContext"
import { useAuth } from "@/state/AuthContext"
import { useFeed } from "@/state/FeedContext"
import { useToast } from "@/state/ToastContext"
import { supportsSearch } from "@/nostr/relayInfo"
import { normalizeRelayUrl, simplifyUrl, toHttpUrl } from "@/utils/url"
import { NoteList } from "@/components/NoteList"
import { Avatar, RelayIcon } from "@/components/Avatar"
import { StarRating } from "@/components/StarRating"
import { Icon } from "@/components/Icon"
import { Timestamp } from "@/components/Timestamp"
import { Spinner } from "@/components/Feedback"
import { NotFoundPage } from "./NotFoundPage"

/** Relay detail page: NIP-11 metadata, live status, stars, reviews and its feed. */
export function RelayPage() {
  const { t } = useTranslation()
  const params = useParams()
  const navigate = useNavigate()
  const { push } = useToast()
  const { canSign } = useAuth()
  const { favoriteRelays, addFavoriteRelays, removeFavoriteRelays } = useRelays()
  const { statuses } = useNostr()
  const { switchToRelay } = useFeed()
  const { reviewRelay } = usePublish()

  const url = useMemo(
    () => normalizeRelayUrl(params.url ? decodeURIComponent(params.url) : ""),
    [params.url],
  )
  const { info, loading } = useRelayInfo(url)
  const reviews = useRelayReviews(url)
  const [search, setSearch] = useState("")
  const [debounced, setDebounced] = useState("")
  const [reviewText, setReviewText] = useState("")
  const [stars, setStars] = useState(0)
  const [publishing, setPublishing] = useState(false)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), 700)
    return () => clearTimeout(timer)
  }, [search])

  const timeline = useTimeline({
    relays: url ? [url] : [],
    search: debounced || undefined,
    enabled: Boolean(url),
  })

  const status = statuses.find((item) => item.url === url)
  const favorite = url ? favoriteRelays.includes(url) : false

  if (!url) return <NotFoundPage />

  const onReview = async () => {
    setPublishing(true)
    const outcome = await reviewRelay(url, reviewText, stars)
    setPublishing(false)
    if (outcome.ok) {
      setReviewText("")
      setStars(0)
    }
  }

  const copy = async (value: string, message: string) => {
    try {
      await navigator.clipboard.writeText(value)
      push("success", message)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      push("error", "Could not copy")
    }
  }

  return (
    <div className="page relay-page">
      <div className="page-topbar">
        <button type="button" className="btn btn-ghost btn-small" onClick={() => navigate(-1)}>
          <Icon name="arrow-left" size={16} /> {t("Go back")}
        </button>
      </div>

      <section className="relay-hero">
        <div className="relay-hero-head">
          <RelayIcon url={url} icon={info?.icon} size={56} />
          <div className="relay-hero-title">
            <h1>{info?.name?.trim() || simplifyUrl(url)}</h1>
            <button
              type="button"
              className="relay-url-button"
              onClick={() => void copy(url, "Relay address copied")}
            >
              {simplifyUrl(url)}
              <Icon name={copied ? "check" : "copy"} size={13} />
            </button>
          </div>
          <span
            className={`status-pill status-${
              status?.state === "connected" ? "ok" : status?.state === "error" ? "bad" : "muted"
            }`}
          >
            {status?.state ?? (loading ? "loading" : "idle")}
            {status?.latency ? ` · ${status.latency} ms` : ""}
          </span>
        </div>

        {info?.description && <p className="relay-hero-description">{info.description}</p>}
        {!loading && !info?.description && <p className="muted small">{t("No description")}</p>}

        <div className="relay-hero-stats">
          <div className="relay-stat">
            <span className="relay-stat-value">
              {reviews.stats.average ? reviews.stats.average.toFixed(1) : "—"}
            </span>
            <span className="relay-stat-label">{t("Stars")}</span>
          </div>
          <div className="relay-stat">
            <span className="relay-stat-value">{reviews.stats.count}</span>
            <span className="relay-stat-label">{t("Reviews")}</span>
          </div>
          <div className="relay-stat">
            <span className="relay-stat-value">{info?.supported_nips?.length ?? 0}</span>
            <span className="relay-stat-label">NIPs</span>
          </div>
          <div className="relay-stat">
            <span className="relay-stat-value">{status?.eventsReceived ?? 0}</span>
            <span className="relay-stat-label">{t("Events received")}</span>
          </div>
        </div>

        <div className="relay-hero-actions">
          <button
            type="button"
            className={`btn ${favorite ? "btn-ghost" : "btn-primary"}`}
            onClick={() => (favorite ? void removeFavoriteRelays([url]) : void addFavoriteRelays([url]))}
          >
            <Icon name={favorite ? "check" : "plus"} size={16} />
            {favorite ? t("In my relays") : t("Add to my relays")}
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              switchToRelay(url)
              navigate("/")
            }}
          >
            <Icon name="relay" size={16} /> {t("Read from this relay")}
          </button>
          <a className="btn btn-ghost" href={toHttpUrl(url)} target="_blank" rel="noopener noreferrer">
            <Icon name="external" size={16} /> NIP-11
          </a>
        </div>

        {info && (
          <dl className="relay-details">
            {info.software && (
              <>
                <dt>{t("Software")}</dt>
                <dd>
                  {info.software}
                  {info.version ? ` v${info.version}` : ""}
                </dd>
              </>
            )}
            {info.contact && (
              <>
                <dt>{t("Contact")}</dt>
                <dd>{info.contact}</dd>
              </>
            )}
            {info.limitation?.auth_required && (
              <>
                <dt>{t("Access")}</dt>
                <dd>authentication required (NIP-42)</dd>
              </>
            )}
            {info.payments_url && (
              <>
                <dt>{t("Payments")}</dt>
                <dd>
                  <a href={info.payments_url} target="_blank" rel="noopener noreferrer">
                    {info.payments_url}
                  </a>
                </dd>
              </>
            )}
            <dt>{t("Supported NIPs")}</dt>
            <dd className="nip-list">
              {(info.supported_nips ?? []).length === 0 && <span className="muted">—</span>}
              {(info.supported_nips ?? []).map((nip) => (
                <span key={nip} className="badge">
                  {nip}
                </span>
              ))}
            </dd>
          </dl>
        )}
      </section>

      <section className="relay-feed-section">
        <div className="section-head">
          <h2>{t("Relay feed")}</h2>
          <span className="muted small">{timeline.events.length} notes loaded</span>
        </div>

        {supportsSearch(info) && (
          <div className="relay-filter">
            <Icon name="search" size={16} />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t("Search this relay (NIP-50)")}
              aria-label={t("Search this relay")}
            />
            {search && (
              <button
                type="button"
                className="icon-button small"
                onClick={() => setSearch("")}
                aria-label="Clear"
              >
                <Icon name="x" size={14} />
              </button>
            )}
          </div>
        )}

        {timeline.closeReasons.length > 0 && (
          <div className="banner banner-info" role="status">
            <Icon name="info" size={16} />
            <p>{timeline.closeReasons.join(" · ")}</p>
          </div>
        )}

        <NoteList
          events={timeline.events}
          loading={timeline.loading}
          loadingMore={timeline.loadingMore}
          hasMore={timeline.hasMore}
          onLoadMore={timeline.loadMore}
          emptyTitle={debounced ? t("No results") : t("No notes from this relay")}
          emptyDescription={
            debounced
              ? t("This relay did not return any notes for that search.")
              : t("The relay is empty, unreachable or requires authentication.")
          }
        />
      </section>


      <section className="relay-reviews-section">
        <div className="section-head">
          <h2>{t("Relay reviews")}</h2>
          <StarRating value={reviews.stats.average} />
        </div>

        {canSign ? (
          <form
            className="review-form"
            onSubmit={(event) => {
              event.preventDefault()
              void onReview()
            }}
          >
            <label className="review-form-stars">
              <span>{t("Stars")}</span>
              <StarRating value={stars} onChange={setStars} />
            </label>
            <textarea
              value={reviewText}
              onChange={(event) => setReviewText(event.target.value)}
              placeholder={t("Write a review…")}
              rows={3}
              aria-label={t("Write a review")}
            />
            <button type="submit" className="btn btn-primary" disabled={publishing || stars === 0}>
              {publishing ? t("Publishing") : t("Publish review")}
            </button>
          </form>
        ) : (
          <p className="muted small">Log in to write a review.</p>
        )}

        {reviews.loading && reviews.reviews.length === 0 && <Spinner />}
        <ReviewList reviews={reviews.reviews} />
      </section>
    </div>
  )
}

function ReviewList({ reviews }: { reviews: ReturnType<typeof useRelayReviews>["reviews"] }) {
  const { t } = useTranslation()
  const profiles = useProfiles(reviews.map((review) => review.pubkey))
  if (reviews.length === 0) {
    return <p className="muted small">{t("No relay reviews yet")}</p>
  }
  return (
    <ul className="review-list">
      {reviews.map((review) => {
        const profile = profiles.get(review.pubkey)
        return (
          <li key={`${review.pubkey}-${review.createdAt}`} className="review-card">
            <div className="review-head">
              <Avatar src={profile?.picture} label={profile?.displayName || review.pubkey} size={24} />
              <Link to={`/u/${profile?.npub ?? review.pubkey}`} className="review-author">
                {profile?.displayName || profile?.name || `${review.pubkey.slice(0, 10)}…`}
              </Link>
              <StarRating value={review.stars} />
              <Timestamp seconds={review.createdAt} className="muted small" />
            </div>
            {review.content && <p className="review-content">{review.content}</p>}
          </li>
        )
      })}
    </ul>
  )
}
